import { useDispatch, useSelector } from 'react-redux';

import { setApiResponseMeta } from '../../common/application/apiResponseMetaSlice';
import { setApiResponseStatus } from '../../common/application/apiResponseStatusSlice';
import useGroupApi from '../../group/data/groupApi';
import type { Group } from '../../group/domain/group';
import type { Parking } from '../../parking/domain/parking';

import type { RootState } from '#root//rootState';
import type User from '@/auth/domain/auth';
import { setSuperGroup, setParkingCtx, updateSuperGroupConfigField, setCompanies, setParkings, getRole, setRole, setSuperGroupAdmin, setSuperResaClass, getRoles, UserRole, unsetRole } from '@/common/application/contextSlice';
import { setLoader, unsetLoader } from '@/common/application/toastSlice';
import type { ApiResponseMeta } from '@/common/domain/api';
import { logException, logTrace, SeverityLevel } from '@/common/utils/logger';
import { useGroupStore } from '@/group/data/groupStore';
import type { GroupConfigEnterprise } from '@/group/domain/groupConfig';
import { setParking } from '@/parking/application/parkingSlice';
import { useParkingStore } from '@/parking/data/parkingStore';
import { useCompany } from '@/subscription/application/companiesController';


// The company the user last asked for, shared by every `useProfileSelection` consumer (the selection
// is global). `setSelectedCompany` resolves the company, its admin group and its reservation class
// before it can dispatch, so two selections in quick succession — the company dropdown fires one on
// every change, then the confirm button fires another — can resolve out of order. Without this token
// the SLOWER (older) selection would dispatch last and the context would settle on a company the user
// did not pick, sending every screen off to load the wrong subscriber's data.
let lastRequestedCompany: string | undefined;

export const useProfileSelection = () => {


    const dispatch = useDispatch();
    const { getClientResaClass } = useCompany();

    const role = useSelector((state: RootState) => state.context.role);
    const filteredCompanies = useSelector((state: RootState) => state.context.filteredCompanies);
    const superGroup = useSelector((state: RootState) => state.context.superGroup);
    const { apiUpdateGroupConfig } = useGroupApi();
    const { getParkings: getPkgs } = useParkingStore();

    const {enterpriseGroups, getById } = useGroupStore();
    
    const getCompanies = async () => {
        try {
            const response = await enterpriseGroups();
            dispatch(setCompanies(response));

            /*
             *dispatch(setApiResponseStatus(response.status));
             *dispatch(setApiResponseMeta(response.meta as ApiResponseMeta | null));
             */
            return response;
        } catch (error: any) {
            logException(error);

            // dispatch(toggleToast({ code: error.response?.status, requestType: RequestTypeEnum.getCompanies, text: error.response?.data?.error?.message }));
        }
    };


    const setSelectedCompany = async (company: Group) => {
        // A LocalAdmin must stay within the subscriptions they actually administer — skipped when
        // filteredCompanies isn't loaded yet, matching the "don't hide anything" fallback used
        // elsewhere for missing scoping info.
        if (role === UserRole.LocalAdmin && filteredCompanies?.length
            && !filteredCompanies.some(c => c.documentId === company.documentId || (c.id != null && c.id === company.id))) {
            logTrace('profile-selection-blocked-company', SeverityLevel.Warning, { companyId: company.documentId });
            return;
        }

        const requested = company.documentId;
        lastRequestedCompany = requested;
        // True once a newer selection has been made: this call's results are obsolete and must not
        // reach the context (see `lastRequestedCompany`).
        const isSuperseded = () => lastRequestedCompany !== requested;

        const groupWithMembers = await getById(company.documentId!);
        try {
            const grpConfig = company.groupConfigs?.find(itm => itm.groupConfig.__component === 'user.enterprise')?.groupConfig as GroupConfigEnterprise;
            if (grpConfig?.superAdminsGroup?.documentId) {
                const groupAdminWithMembers = await getById(grpConfig.superAdminsGroup.documentId);
                if (!isSuperseded()) dispatch(setSuperGroupAdmin(groupAdminWithMembers));
            }
        } catch (e) {
            logException(e, { companyId: company.documentId });
        }
        const resaClass = await getClientResaClass(company.documentId ?? company.id as number);
        if (isSuperseded()) return;
        dispatch(setSuperResaClass(resaClass));

        // settingSuperGroupe
        if (groupWithMembers) {

            dispatch(setSuperGroup(groupWithMembers));
        }
    };

    const getParkings = async () => {
        try {
            dispatch(setLoader());
            const response = await getPkgs();
            dispatch(setParkings(response ?? []));
            
            dispatch(unsetLoader());
            return response;
        } catch (error) {
            dispatch(unsetLoader());

            logException(error);
        }
    };

    const setSelectedParking = (parking: Parking) => {
        dispatch(setParkingCtx(parking));
        dispatch(setParking(parking));
    };

    const updateSuperGroupConfigFieldAction = (field: string, value: any) => {
        dispatch(updateSuperGroupConfigField({ field, value }));
    };

    const updateSuperGroupConfig = async (config: GroupConfigEnterprise) => {
        try {
            const response = await apiUpdateGroupConfig({ ...superGroup?.groupConfigs, groupConfig: [config] });
            dispatch(setApiResponseStatus(response.status));
            dispatch(setApiResponseMeta(response.meta as ApiResponseMeta | null));
            return response.data;
        } catch (error) {
            logException(error, { groupId: superGroup?.id });
        }
    };

    // Method that will set the user role depending on its appartenence to a group, an admin group or the admin group of a super group
    const getRoleAction = (user: User) => {
        if (!user?.ref) return;
        dispatch(getRole(user.ref));
    };

    const getRolesAction = (user: User) => {
        if (!user?.ref) return;
        dispatch(getRoles(user.ref));
    };

    const setRoleAction = (user?: User, role?: UserRole) => {
        dispatch(setRole({ role: role, user: user?.ref }));
    };

    const unsetRoleAction = () => {
        dispatch(unsetRole());
    };

    return { getCompanies, getParkings, getRoleAction, getRolesAction, setRoleAction, setSelectedCompany, setSelectedParking, unsetRoleAction, updateSuperGroupConfig, updateSuperGroupConfigFieldAction };
};
